import {
  MetricLineChart,
  getDateRangeDescription,
} from "@/components/charts/metricsLineChart";
import { cn } from "@/lib/utils/ui";
import { createFileRoute } from "@tanstack/react-router";
import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import * as luxon from "luxon";
import {
  ArrowDown,
  ChartColumnIncreasingIcon,
  MoreHorizontal,
  Plus,
  Search,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import EditMetricLogDialog, {
  MetricLog,
} from "@/components/metricsComponents/editMetricLogDialog";
import {
  Metric,
  WEIGHT_METRIC_ID,
} from "@/components/metricsComponents/metricsColumns";
import AssignClientMetricDialog from "@/components/metricsComponents/assignClientMetricDialog";
import { TbTargetArrow } from "react-icons/tb";
import MetricDailyTargetDialog from "@/components/metricsComponents/MetricDailyTargetDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc/client";
import { ClientSidebarDetailLoadingState } from "@/components/client/ClientTabLoadingStates";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute(
  "/_auth/_dashboard/clients/$clientId/_layout/metrics",
)({
  component: Metrics,
  pendingComponent: ClientSidebarDetailLoadingState,
});

const STEPS_METRIC_ID = "03a40121-31c4-42da-b259-a60781dcb32e";
const RECENT_LOGS_LIMIT = 8;

export type ClientMetricLog = {
  metric_id: string;
  value: number;
  logged_at: string;
};

export type MetricSummary = {
  currentLog?: ClientMetricLog;
  startLog?: ClientMetricLog;
  average?: number;
  changeValue?: number;
  changePercent?: number;
  sign: -1 | 0 | 1;
};

function Metrics() {
  const { clientId } = Route.useParams();
  const { data, isLoading } = trpc.metrics.listForClient.useQuery(
    { clientId },
    { enabled: Boolean(clientId) },
  );

  const [selectedMetric, setSelectedMetric] = useState<Metric>();
  const [assignClientMetricDialogOpen, setAssignClientMetricDialogOpen] =
    useState(false);
  const [search, setSearch] = useState("");

  const visibleMetrics = useMemo(() => {
    return (data ?? [])
      .filter((metric) => metric.id !== STEPS_METRIC_ID)
      .map(toMetric);
  }, [data]);

  const filteredMetrics = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return visibleMetrics;
    }
    return visibleMetrics.filter((metric) =>
      metric.name.toLowerCase().includes(query),
    );
  }, [visibleMetrics, search]);

  const metricSummaries = useMemo(() => {
    const summaries = new Map<string, MetricSummary>();
    for (const metric of data ?? []) {
      if (!metric.id || metric.id === STEPS_METRIC_ID) continue;
      summaries.set(metric.id, summaryFromAssignment(metric));
    }
    return summaries;
  }, [data]);

  useEffect(() => {
    setSelectedMetric((current) => {
      if (visibleMetrics.length === 0) {
        return undefined;
      }
      return (
        visibleMetrics.find((metric) => metric.id === current?.id) ??
        visibleMetrics[0]
      );
    });
  }, [visibleMetrics]);

  const weightUnitLabel =
    data?.[0]?.client_weight_unit === "lbs" ? "lbs" : "kg";
  const getMetricUnit = (metric: Metric) => {
    if (metric.id === WEIGHT_METRIC_ID) {
      return weightUnitLabel;
    }
    return metric.unit ?? "";
  };
  const selectedMetricWithUnit = useMemo(() => {
    if (!selectedMetric) {
      return undefined;
    }
    return { ...selectedMetric, unit: getMetricUnit(selectedMetric) };
  }, [selectedMetric, weightUnitLabel]);

  if (isLoading) {
    return <ClientSidebarDetailLoadingState />;
  }

  return (
    <>
      <AssignClientMetricDialog
        clientId={clientId}
        open={assignClientMetricDialogOpen}
        onOpenChange={setAssignClientMetricDialogOpen}
        onMetricAdded={(metric) => setSelectedMetric(metric)}
      />
      <div className="mb-6 w-full md:w-[90%] mx-auto px-4 lg:px-0">
        {visibleMetrics.length === 0 && (
          <Empty className="py-16">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <ChartColumnIncreasingIcon className="size-6" />
              </EmptyMedia>
              <EmptyTitle>No metrics yet</EmptyTitle>
              <EmptyDescription>
                Add a metric to track this client&apos;s progress.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button
                variant="outline"
                onClick={() => setAssignClientMetricDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" /> Add First Metric
              </Button>
            </EmptyContent>
          </Empty>
        )}
        {visibleMetrics.length > 0 && (
          <>
            <div className="lg:hidden mb-4 space-y-3">
              <div className="flex items-center gap-2">
                <Select
                  value={selectedMetric?.id || ""}
                  onValueChange={(value) => {
                    const metric = visibleMetrics.find((m) => m.id === value);
                    if (metric) setSelectedMetric(metric);
                  }}
                >
                  <SelectTrigger className="h-10 w-full">
                    <SelectValue placeholder="Select a metric" />
                  </SelectTrigger>
                  <SelectContent>
                    {visibleMetrics.map((metric) => (
                      <SelectItem key={metric.id} value={metric.id}>
                        {metric.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="icon"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => setAssignClientMetricDialogOpen(true)}
                  aria-label="Add metric"
                >
                  <Plus className="size-4" />
                </Button>
              </div>
            </div>

            <div className="w-full grid grid-cols-1 lg:grid-cols-[19rem_minmax(0,1fr)] gap-5">
              <Card className="hidden lg:flex sticky top-5 h-[calc(100svh-5.25rem)] max-h-[calc(100svh-5.25rem)] flex-col overflow-hidden">
                <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-3">
                  <h2 className="text-lg font-semibold tracking-tight">
                    Metrics
                  </h2>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="size-8"
                    onClick={() => setAssignClientMetricDialogOpen(true)}
                    aria-label="Add metric"
                  >
                    <Plus className="size-4" />
                  </Button>
                </div>
                <div className="px-4 pb-3">
                  <InputGroup>
                    <InputGroupAddon>
                      <Search className="size-4 text-muted-foreground" />
                    </InputGroupAddon>
                    <InputGroupInput
                      placeholder="Search metrics"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                    />
                  </InputGroup>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
                  {filteredMetrics.length === 0 ? (
                    <p className="px-1 py-8 text-center text-sm text-muted-foreground">
                      No matching metrics
                    </p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {filteredMetrics.map((metric) => {
                        const unit = getMetricUnit(metric);
                        const summary = metricSummaries.get(metric.id);
                        const selected = selectedMetric?.id === metric.id;

                        return (
                          <button
                            key={metric.id}
                            type="button"
                            className={cn(
                              "w-full rounded-lg border p-3 text-left transition-colors hover:bg-muted/50",
                              selected
                                ? "border-primary bg-primary/5"
                                : "border-border bg-card",
                            )}
                            onClick={() => setSelectedMetric(metric)}
                          >
                            <div className="min-w-0">
                              <h3 className="truncate font-medium leading-snug">
                                {metric.name}
                              </h3>
                              <div className="mt-2 flex min-w-0 items-baseline justify-between gap-2">
                                <span className="truncate text-sm text-muted-foreground">
                                  {formatCurrentMetricValue(summary, unit)}
                                </span>
                                {summary?.currentLog && summary.sign !== 0 && (
                                  <ChangeValue
                                    value={summary.changeValue}
                                    unit={unit}
                                    className="shrink-0 text-sm"
                                  />
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Card>
              <div className="min-w-0">
                {selectedMetricWithUnit && (
                  <MetricDetails
                    metric={selectedMetricWithUnit}
                    clientId={clientId}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

interface MetricDetailsProps {
  metric: Metric;
  clientId: string;
  dailyTargetDialogClassName?: string;
}

export const MetricDetails: FunctionComponent<MetricDetailsProps> = ({
  metric,
  clientId,
  dailyTargetDialogClassName,
}) => {
  const [selectedLog, setSelectedLog] = useState<MetricLog>();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [dailyTargetOpen, setDailyTargetOpen] = useState(false);
  const [showAllLogs, setShowAllLogs] = useState(false);
  const logsSectionRef = useRef<HTMLDivElement>(null);

  const [weeksForRange, setWeeksForRange] = useState<number | null>(2);

  const fromDate = useMemo(() => {
    if (weeksForRange === null) {
      return null;
    }
    return luxon.DateTime.now().minus({ weeks: weeksForRange }).toISO();
  }, [weeksForRange]);

  const { data } = trpc.metrics.listLogs.useQuery(
    { clientId, metricId: metric.id },
    { enabled: Boolean(clientId && metric.id) },
  );

  useEffect(() => {
    setShowAllLogs(false);
  }, [metric.id]);

  const rangeLogs = useMemo(() => {
    if (!data) return [];
    if (!fromDate) return data;
    return data.filter((log) => new Date(fromDate) < new Date(log.logged_at));
  }, [data, fromDate]);

  const allLogsAscending = useMemo(() => {
    return (data ?? [])
      .map((log) => ({
        metric_id: metric.id,
        value: log.value,
        logged_at: log.logged_at,
      }))
      .sort(
        (a, b) =>
          new Date(a.logged_at).getTime() - new Date(b.logged_at).getTime(),
      );
  }, [data, metric.id]);

  const rangeLogsAscending = useMemo(() => {
    return rangeLogs
      .map((log) => ({
        metric_id: metric.id,
        value: log.value,
        logged_at: log.logged_at,
      }))
      .sort(
        (a, b) =>
          new Date(a.logged_at).getTime() - new Date(b.logged_at).getTime(),
      );
  }, [rangeLogs, metric.id]);

  const overallSummary = useMemo(
    () => getMetricSummary(allLogsAscending),
    [allLogsAscending],
  );
  const rangeSummary = useMemo(
    () => getMetricSummary(rangeLogsAscending),
    [rangeLogsAscending],
  );

  const chartData = useMemo(() => {
    return rangeLogsAscending.map((log) => ({
      date: log.logged_at,
      value: log.value,
    }));
  }, [rangeLogsAscending]);

  const logsWithChange = useMemo(() => {
    const descending = [...allLogsAscending].reverse();
    return descending.map((log, index) => {
      const previous = descending[index + 1];
      return {
        ...log,
        change: previous ? log.value - previous.value : undefined,
      };
    });
  }, [allLogsAscending]);

  const visibleLogs = showAllLogs
    ? logsWithChange
    : logsWithChange.slice(0, RECENT_LOGS_LIMIT);
  const canShowAllLogs = logsWithChange.length > RECENT_LOGS_LIMIT;

  const handleOnClickEdit = (log: MetricLog) => {
    setSelectedLog(log);
    setEditDialogOpen(true);
  };

  const handleOpenChange = (open: boolean) => {
    setEditDialogOpen(open);
    if (!open) {
      setSelectedLog(undefined);
    }
  };

  const scrollToLogs = () => {
    setShowAllLogs(true);
    logsSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const canLogManually = !metric.os_integration_type;
  const logButtonLabel = `Log ${metric.name.toLowerCase()}`;

  return (
    <>
      <EditMetricLogDialog
        clientId={clientId}
        metric={metric}
        log={selectedLog}
        open={editDialogOpen}
        onOpenChange={handleOpenChange}
      />
      <MetricDailyTargetDialog
        clientId={clientId}
        metric={metric}
        open={dailyTargetOpen}
        contentClassName={dailyTargetDialogClassName}
        onOpenChange={setDailyTargetOpen}
      />
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="truncate text-3xl font-semibold tracking-tight">
              {metric.name}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {formatLastLogged(overallSummary.currentLog?.logged_at)}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {metric.os_integration_type && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="More actions"
                  >
                    <MoreHorizontal className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setDailyTargetOpen(true)}>
                    <TbTargetArrow className="size-4" />
                    Daily target
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button variant="outline" onClick={scrollToLogs}>
              View logs
            </Button>
            {canLogManually && (
              <Button onClick={() => setEditDialogOpen(true)}>
                {logButtonLabel}
              </Button>
            )}
          </div>
        </div>

        <MetricSummaryStats
          metric={metric}
          current={overallSummary}
          range={rangeSummary}
          rangeLabel={getDateRangeDescription(weeksForRange)}
        />

        <MetricLineChart
          metric={metric}
          data={chartData}
          onDateRangeWeeksChanged={setWeeksForRange}
          dateRangeWeeksValue={weeksForRange}
          hideHeader
          hideSummaryPills
        />

        <Card ref={logsSectionRef} className="scroll-mt-4 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-4 sm:px-6">
            <h2 className="text-base font-semibold">Recent logs</h2>
          </div>
          <div className={cn(showAllLogs && "max-h-[28rem] overflow-auto")}>
            <Table>
              <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>
                  <span className="inline-flex items-center gap-1">
                    Date
                    <ArrowDown className="size-3" />
                  </span>
                </TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Change</TableHead>
                <TableHead className="w-12 text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleLogs.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={4}
                    className="h-20 text-center text-muted-foreground"
                  >
                    No logs yet
                  </TableCell>
                </TableRow>
              ) : (
                visibleLogs.map((log) => (
                  <TableRow key={log.logged_at}>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {formatLogDateTime(log.logged_at)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatMetricValue(log.value, metric.unit)}
                    </TableCell>
                    <TableCell>
                      {log.change === undefined ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <ChangeValue value={log.change} unit={metric.unit} />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8"
                            aria-label="Log actions"
                          >
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleOnClickEdit(log)}
                          >
                            Edit
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            </Table>
          </div>
          {canShowAllLogs && (
            <div className="border-t px-4 py-3 text-center">
              <Button
                variant="link"
                className="h-auto p-0 text-sm"
                onClick={() => setShowAllLogs((current) => !current)}
              >
                {showAllLogs ? "Show recent logs" : "View all logs"}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </>
  );
};

export function MetricSummaryStats({
  metric,
  current,
  range,
  rangeLabel,
}: {
  metric: Metric;
  current: MetricSummary;
  range: MetricSummary;
  rangeLabel: string;
}) {
  return (
    <div className="grid grid-cols-3 gap-3 sm:gap-0">
      <div className="min-w-0">
        <p className="text-xs font-medium text-muted-foreground sm:text-sm">
          Current
        </p>
        <p className="mt-1 truncate text-xl font-semibold tracking-tight sm:text-2xl">
          {formatMetricValue(current.currentLog?.value, metric.unit, "—")}
        </p>
      </div>
      <div className="min-w-0 border-border/80 sm:border-l sm:px-6">
        <p className="text-xs font-medium text-muted-foreground sm:text-sm">
          Change
        </p>
        <div className="mt-1 text-xl font-semibold tracking-tight sm:text-2xl">
          <ChangeValue
            value={range.changeValue}
            unit={metric.unit}
            empty="—"
            className="text-xl sm:text-2xl"
          />
        </div>
        {range.startLog && (
          <p className="mt-1 truncate text-xs text-muted-foreground">
            vs {formatShortDate(range.startLog.logged_at)}
          </p>
        )}
      </div>
      <div className="min-w-0 border-border/80 sm:border-l sm:px-6">
        <p className="text-xs font-medium text-muted-foreground sm:text-sm">
          Average
        </p>
        <p className="mt-1 truncate text-xl font-semibold tracking-tight sm:text-2xl">
          {formatMetricValue(range.average, metric.unit, "—")}
        </p>
        <p className="mt-1 truncate text-xs text-muted-foreground">
          {rangeLabel}
        </p>
      </div>
    </div>
  );
}

function toMetric(metric: {
  id: string;
  name: string | null;
  unit: string | null;
  org_id: string | null;
  os_integration_type: string | null;
  daily_target: number | null;
}): Metric {
  return {
    id: metric.id,
    name: metric.name ?? "",
    unit: metric.unit,
    org_id: metric.org_id ?? "",
    os_integration_type: metric.os_integration_type,
    daily_target: metric.daily_target,
  };
}

function summaryFromAssignment(metric: {
  id: string;
  first_value_ever: number | null;
  first_logged_at_ever: string | null;
  last_value_ever: number | null;
  last_logged_at_ever: string | null;
}): MetricSummary {
  const logs: ClientMetricLog[] = [];
  if (metric.first_value_ever != null && metric.first_logged_at_ever) {
    logs.push({
      metric_id: metric.id,
      value: metric.first_value_ever,
      logged_at: metric.first_logged_at_ever,
    });
  }
  if (
    metric.last_value_ever != null &&
    metric.last_logged_at_ever &&
    metric.last_logged_at_ever !== metric.first_logged_at_ever
  ) {
    logs.push({
      metric_id: metric.id,
      value: metric.last_value_ever,
      logged_at: metric.last_logged_at_ever,
    });
  } else if (
    logs.length === 0 &&
    metric.last_value_ever != null &&
    metric.last_logged_at_ever
  ) {
    logs.push({
      metric_id: metric.id,
      value: metric.last_value_ever,
      logged_at: metric.last_logged_at_ever,
    });
  }

  return getMetricSummary(logs);
}

export function getMetricSummary(logs: ClientMetricLog[]): MetricSummary {
  if (logs.length === 0) {
    return { sign: 0 };
  }

  const startLog = logs[0];
  const currentLog = logs[logs.length - 1];
  const average = logs.reduce((sum, log) => sum + log.value, 0) / logs.length;
  const changeValue = currentLog.value - startLog.value;
  const changePercent =
    startLog.value !== 0 ? (changeValue / Math.abs(startLog.value)) * 100 : 0;
  const sign = changeValue > 0 ? 1 : changeValue < 0 ? -1 : 0;

  return {
    currentLog,
    startLog,
    average,
    changeValue,
    changePercent,
    sign,
  };
}

function formatNumber(value: number, fixed = 1) {
  if (Number.isInteger(value)) {
    return value.toLocaleString();
  }
  return value.toFixed(fixed);
}

function formatMetricValue(
  value: number | undefined,
  unit: string | null,
  empty = "No logs",
) {
  if (value === undefined) {
    return empty;
  }

  return `${formatNumber(value)}${unit ? ` ${unit}` : ""}`;
}

function formatCurrentMetricValue(
  summary: MetricSummary | undefined,
  unit: string | null,
) {
  return formatMetricValue(summary?.currentLog?.value, unit);
}

function formatSignedNumber(value: number, fixed = 1) {
  const formatted = formatNumber(Math.abs(value), fixed);
  if (value > 0) {
    return `+${formatted}`;
  }
  if (value < 0) {
    return `-${formatted}`;
  }
  return formatted;
}

function ChangeValue({
  value,
  unit,
  empty,
  className,
}: {
  value: number | undefined;
  unit: string | null;
  empty?: string;
  className?: string;
}) {
  if (value === undefined) {
    return empty ? (
      <span className={cn("text-muted-foreground", className)}>{empty}</span>
    ) : null;
  }

  const tone =
    value === 0 ? "text-muted-foreground" : "text-primary";

  return (
    <span className={cn("font-semibold", tone, className)}>
      {formatSignedNumber(value)}
      {unit ? ` ${unit}` : ""}
    </span>
  );
}

export function formatLastLogged(loggedAt: string | undefined) {
  if (!loggedAt) {
    return "Not logged yet";
  }

  const date = luxon.DateTime.fromISO(loggedAt);
  const time = date.toFormat("h:mm a");
  const now = luxon.DateTime.now();

  if (date.hasSame(now, "day")) {
    return `Last logged today at ${time}`;
  }
  if (date.hasSame(now.minus({ days: 1 }), "day")) {
    return `Last logged yesterday at ${time}`;
  }

  return `Last logged ${date.toFormat("d LLL")} at ${time}`;
}

function formatLogDateTime(loggedAt: string) {
  return luxon.DateTime.fromISO(loggedAt).toFormat("d LLL yyyy, h:mm a");
}

function formatShortDate(loggedAt: string) {
  return luxon.DateTime.fromISO(loggedAt).toFormat("d LLL yyyy");
}
